'use client';

import { useEffect } from 'react';

/**
 * Rewrites broken team/league deep links inside SI live-banner widgets.
 *
 * The SI account config (widgets.si-ab.com/c/<acid>/config.js) sets
 *   teamDeepLink   = "easyodds_teams"
 *   leagueDeepLink = "easyodds_tournaments"
 * where a URL template belongs. The widget bundle does plain placeholder
 * substitution into <a href> (verified in bundle.easyodds.js — no lookup, no
 * fallback), so every team/league anchor it renders gets that literal token
 * as its href and resolves to /easyodds_teams on our origin.
 *
 * The widgets render into OPEN shadow roots, so we can reach in and repair
 * the anchors: a team anchor's visible text is the team name, which our
 * /teams/resolve endpoint maps to the canonical team page. Each widget's
 * shadow root gets a MutationObserver so anchors are re-fixed whenever the
 * widget re-renders.
 *
 * This is a shim around a third-party misconfiguration. It becomes redundant
 * (and harmless — it only touches the exact token hrefs) once the SI
 * dashboard sets a real template, e.g.
 *   teamDeepLink = https://www.easyodds.com/teams/resolve?id=${teamId}&name=${name}
 */

const BROKEN_TEAM_HREF = 'easyodds_teams';
const BROKEN_LEAGUE_HREF = 'easyodds_tournaments';
const FALLBACK = '/football-teams';

const fixAnchor = (a: Element): void => {
    const href = a.getAttribute('href');
    if (href !== BROKEN_TEAM_HREF && href !== BROKEN_LEAGUE_HREF) return;
    const name = (a.textContent || '').trim();
    a.setAttribute(
        'href',
        href === BROKEN_TEAM_HREF && name
            ? `/teams/resolve?name=${encodeURIComponent(name)}`
            : FALLBACK
    );
};

export default function SiWidgetLinkFix() {
    useEffect(() => {
        const observers: MutationObserver[] = [];
        const attached = new WeakSet<Element>();

        const sweep = (root: ShadowRoot) => {
            root.querySelectorAll('a[href]').forEach(fixAnchor);
        };

        const attach = (el: Element) => {
            const root = el.shadowRoot;
            // shadowRoot appears only after the SI bundle upgrades the element,
            // so unattached widgets are retried on the next tick.
            if (!root || attached.has(el)) return;
            attached.add(el);
            sweep(root);
            const mo = new MutationObserver(() => sweep(root));
            mo.observe(root, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['href'],
            });
            observers.push(mo);
        };

        // Widgets arrive with CMS content on any page, at any point during
        // client-side navigation, and upgrade whenever the SI bundle gets to
        // them — a light periodic scan handles all of those cases.
        const tick = () => document.querySelectorAll('si-lb-widget').forEach(attach);
        tick();
        const interval = setInterval(tick, 1000);

        return () => {
            clearInterval(interval);
            observers.forEach((mo) => mo.disconnect());
        };
    }, []);

    return null;
}
